import { useState } from "react";
import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { ConfirmDialog, DashboardCard } from "@tellescope/ui";
import { formatDateTime } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { cn } from "@tellescope/design-system";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export interface OrdersRecordsListProps {
  contact: TellescopeModelFromName<"endusers">;
}

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" => {
  const normalized = status.toLowerCase();
  if (normalized.includes("deliver") || normalized.includes("complete")) return "default";
  if (normalized.includes("ship") || normalized.includes("transit")) return "default";
  if (normalized.includes("pending") || normalized.includes("process")) return "secondary";
  if (normalized.includes("error") || normalized.includes("cancel") || normalized.includes("fail"))
    return "destructive";
  return "secondary";
};

export const OrdersRecordsList = ({ contact }: OrdersRecordsListProps) => {
  const { t } = useTranslation("contacts");
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);

  const orderStatusLabels: Record<string, string> = {
    pending: t("Pending"),
    processing: t("Processing"),
    shipped: t("Shipped"),
    delivered: t("Delivered"),
    cancelled: t("Cancelled"),
    canceled: t("Cancelled"),
    completed: t("Completed"),
    failed: t("Failed"),
    error: t("Error"),
  };

  const ordersQuery = useTellescopeInfiniteQuery({
    model: "enduser_orders",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const deleteOrderMutation = useTellescopeMutation({
    model: "enduser_orders",
    method: "deleteOne",
    onSuccess: () => {
      setOrderToDelete(null);
    },
  });

  const showMessage = ordersQuery.isLoading || !ordersQuery.data?.length;

  return (
    <div className={cn("flex-1 min-h-0 flex flex-col pr-2", showMessage && "justify-center")}>
      {ordersQuery.isLoading ? (
        <BlockEmptyState>{t("Loading orders...")}</BlockEmptyState>
      ) : ordersQuery.data?.length ? (
        <>
          <BlockPaginatedList
            className="flex flex-col"
            scrollbar="floating"
            fadeWhenClipped
            onBottomReached={() => ordersQuery.fetchNextPage()}
          >
            {ordersQuery.data.map((order) => (
              <DashboardCard
                key={order.id}
                statusLabel={orderStatusLabels[order.status.toLowerCase()] ?? order.status}
                status={getStatusVariant(order.status)}
                title={order.title}
                subtitle={formatDateTime(order.updatedAt, "MM/DD/YYYY at hh:mm a", true)}
                assignees={[]}
                onDelete={() => setOrderToDelete(order.id)}
                deletePosition="status"
                className="w-full mb-4"
              />
            ))}
          </BlockPaginatedList>

          <ConfirmDialog
            open={!!orderToDelete}
            onOpenChange={(open) => !open && setOrderToDelete(null)}
            variant="destructive"
            title={t("Delete Order")}
            description={t("Are you sure you want to delete this order? This action cannot be undone.")}
            confirmText={t("Delete")}
            onConfirm={async () => {
              if (orderToDelete) {
                await deleteOrderMutation.mutateAsync(orderToDelete);
              }
            }}
          />
        </>
      ) : (
        <BlockEmptyState>{t("No orders")}</BlockEmptyState>
      )}
    </div>
  );
};
